<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import OperationsAPI from 'dashboard/api/operations';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const scheduledMessages = ref([]);
const searchQuery = ref('');
const loading = ref(false);

const filteredMessages = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();
  if (!query) return scheduledMessages.value;

  return scheduledMessages.value.filter(item =>
    [
      item.content,
      item.conversation.contact_name,
      item.conversation.inbox_name,
      item.user.name,
    ].some(value => value?.toLocaleLowerCase().includes(query))
  );
});

const formatDate = value =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));

const load = async () => {
  loading.value = true;
  try {
    const { data } = await OperationsAPI.scheduledMessages();
    scheduledMessages.value = data.payload;
  } catch {
    useAlert(t('OPERATIONS.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const openConversation = item =>
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: item.conversation.id },
  });

const cancel = async item => {
  try {
    await OperationsAPI.cancelScheduledMessage(item.id);
    scheduledMessages.value = scheduledMessages.value.filter(
      scheduled => scheduled.id !== item.id
    );
    useAlert(t('OPERATIONS.SCHEDULED.CANCELLED'));
  } catch {
    useAlert(t('OPERATIONS.SCHEDULED.CANCEL_ERROR'));
  }
};

onMounted(load);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :no-records-found="!filteredMessages.length"
    :no-records-message="$t('OPERATIONS.SCHEDULED.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('OPERATIONS.SCHEDULED.TITLE')"
        :description="$t('OPERATIONS.SCHEDULED.DESCRIPTION')"
        :search-placeholder="$t('OPERATIONS.SCHEDULED.SEARCH')"
      >
        <template #actions>
          <Button
            :label="$t('OPERATIONS.REFRESH')"
            icon="i-lucide-refresh-cw"
            color="slate"
            size="sm"
            :is-loading="loading"
            @click="load"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="overflow-hidden border rounded-xl border-n-weak">
        <article
          v-for="item in filteredMessages"
          :key="item.id"
          class="flex flex-col gap-3 p-4 border-b last:border-0 border-n-weak lg:flex-row lg:items-center"
        >
          <button
            class="flex-1 min-w-0 text-left"
            type="button"
            @click="openConversation(item)"
          >
            <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span class="text-sm font-semibold text-n-slate-12">
                #{{ item.conversation.id }} ·
                {{ item.conversation.contact_name }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ item.conversation.inbox_name }}
              </span>
            </div>
            <p class="mt-1 text-sm text-n-slate-11 line-clamp-2">
              {{ item.content }}
            </p>
            <div
              class="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-n-slate-10"
            >
              <span>{{ formatDate(item.scheduled_at) }}</span>
              <span>{{ item.user.name }}</span>
              <span v-if="item.only_if_no_reply">
                {{ $t('OPERATIONS.SCHEDULED.CONDITIONAL') }}
              </span>
              <span v-if="item.attachment_count">
                {{
                  $t('OPERATIONS.SCHEDULED.ATTACHMENTS', {
                    count: item.attachment_count,
                  })
                }}
              </span>
            </div>
          </button>
          <div class="flex gap-2">
            <Button
              :label="$t('OPERATIONS.SCHEDULED.OPEN')"
              icon="i-lucide-message-square"
              color="slate"
              size="sm"
              @click="openConversation(item)"
            />
            <Button
              :label="$t('OPERATIONS.SCHEDULED.CANCEL')"
              icon="i-lucide-x"
              color="ruby"
              size="sm"
              @click="cancel(item)"
            />
          </div>
        </article>
      </div>
    </template>
  </SettingsLayout>
</template>
